/**
 * PATH: packages/email-hub/src/feature/lib/aiCompose.ts
 * FILE_ID: EMAILHUB-FEATURE-LIB-AICOMPOSE-001
 * PURPOSE: Email AI compose-assist helpers — a from-scratch "draft from a
 *          brief" call and selection-scoped polish actions (rewrite / shorten
 *          / tone / grammar) that the editor surfaces on a highlighted range.
 * WHY:     The composer previously only had a reply-only AI draft. Batch 2 of
 *          the Composer Uplift Roadmap adds compose + inline polish; both ride
 *          the SAME `/ai/email/draft-reply` endpoint via a `mode` discriminator
 *          (BE: build_email_draft_messages), so there's one transport to test
 *          and one to keep DeepSeek-parity with.
 * FLOW:
 *   ┌──────────────┐   ┌──────────────────────┐   ┌────────────────────┐
 *   │ brief / sel. │ → │ draftEmailReply(mode) │ → │ {subject?, body}   │
 *   └──────────────┘   └──────────────────────┘   └────────────────────┘
 * DEPENDENCIES:
 *   - ../../_internal/services/deepseek: draftEmailReply (typed BE proxy)
 * RELATED:
 *   - components/rich-text/EditorContextMenu.tsx: renders AI_SELECTION_ACTIONS
 *   - components/AiComposeButton.tsx: from-scratch "Draft with AI" on new intent
 */

import DOMPurify from 'dompurify'
import {
  draftEmailReply, chatCompletion, type EmailDraftMode, type AIMessage,
} from '../../_internal/services/deepseek'

/** Selection-scoped polish modes (a strict subset of EmailDraftMode). */
export type AiSelectionMode = Extract<
  EmailDraftMode,
  'rewrite' | 'shorten' | 'tone' | 'grammar'
>

export interface AiSelectionAction {
  /** Stable id for React keys + tests. */
  id: string
  mode: AiSelectionMode
  label: string
  /** Only meaningful for `tone` — the target tone passed to the BE. */
  tone?: string
}

/**
 * The right-click AI actions offered when the operator has text selected.
 * Tone is split into two concrete choices ("More formal" / "More friendly")
 * so the operator never has to type a tone — one click, one result.
 */
export const AI_SELECTION_ACTIONS: readonly AiSelectionAction[] = [
  { id: 'rewrite', mode: 'rewrite', label: 'Rewrite' },
  { id: 'shorten', mode: 'shorten', label: 'Make shorter' },
  { id: 'grammar', mode: 'grammar', label: 'Fix grammar' },
  { id: 'tone-formal', mode: 'tone', tone: 'formal', label: 'More formal' },
  { id: 'tone-friendly', mode: 'tone', tone: 'friendly', label: 'More friendly' },
] as const

/**
 * Pure-ish I→O: run a selection action over `text`, returning the revised
 * text (trimmed) or null when the model gave nothing usable. Throws only on
 * transport failure (the caller surfaces a toast).
 */
export async function transformSelection(
  action: AiSelectionAction,
  text: string,
): Promise<string | null> {
  const clean = (text || '').trim()
  if (!clean) return null
  const res = await draftEmailReply({
    subject: '',
    body_text: clean,
    from_name: '',
    mode: action.mode,
    selection: clean,
    tone: action.tone,
  })
  return (res?.body || '').trim() || null
}

/**
 * From-scratch compose: turn a one-line brief into a subject + body.
 * Returns null when the model gave nothing usable.
 */
export async function composeFromBrief(
  brief: string,
  opts?: { tone?: string },
): Promise<{ subject: string; body: string } | null> {
  const clean = (brief || '').trim()
  if (!clean) return null
  const res = await draftEmailReply({
    subject: '',
    body_text: clean,
    from_name: '',
    mode: 'compose',
    tone: opts?.tone,
  })
  if (!res) return null
  const subject = (res.subject || '').trim()
  const body = (res.body || '').trim()
  if (!subject && !body) return null
  return { subject, body }
}

// ─── Free-form "Ask AI…" (Notion-style) ──────────────────────────────────
// The preset selection actions (rewrite/shorten/tone/grammar) can't satisfy
// "fill out this table" or "write a formatted email with the site-visit dates
// and times" need a free-form instruction AND structured HTML back
// (lists, tables). So this path rides the generic `/ai/chat` transport (the
// same one the SAM/Newmark AI hubs use) and asks the model for an email-safe
// HTML fragment that we drop straight into the TipTap editor. Output is
// allowlist-sanitised before it ever touches the document.

/** Tags/attrs the editor + email clients understand. Anything else (scripts,
 *  iframes, event handlers, javascript: URLs) is stripped by DOMPurify. */
const AI_HTML_ALLOWED_TAGS = [
  'p', 'br', 'span', 'div', 'strong', 'b', 'em', 'i', 'u', 's',
  'h1', 'h2', 'h3', 'ul', 'ol', 'li', 'blockquote', 'a', 'hr',
  'table', 'thead', 'tbody', 'tfoot', 'tr', 'th', 'td',
]
const AI_HTML_ALLOWED_ATTRS = ['href', 'target', 'rel', 'colspan', 'rowspan', 'style', 'align']

/**
 * Build the chat messages for a free-form editor instruction. Pure I→O so the
 * prompt contract is unit-testable. When `selectionHtml` is non-empty the model
 * is told to operate on (and return the COMPLETE updated form of) that fragment,
 * e.g. a table the operator highlighted and asked to fill in.
 */
export function buildInstructionMessages(instruction: string, selectionHtml: string): AIMessage[] {
  const system =
    'You are a writing assistant embedded in a rich-text EMAIL body editor. '
    + 'Apply the user\'s instruction and reply with ONLY a valid HTML fragment '
    + 'suitable for an email body. Allowed tags: p, br, strong, em, u, s, h2, h3, '
    + 'ul, ol, li, blockquote, a, hr, and table/thead/tbody/tr/th/td for tabular '
    + 'data. Do NOT wrap the output in <html>, <head>, <body>, <script>, <style>, '
    + 'markdown code fences (```), or any commentary; return the fragment only. '
    + 'When the instruction is about a table (fill in, add columns, summarise), '
    + 'return the COMPLETE updated <table>. Keep it concise and professional.'
  const sel = (selectionHtml || '').trim()
  const user = sel
    ? `Instruction: ${instruction.trim()}\n\nSelected HTML to act on:\n${sel}`
    : `Instruction: ${instruction.trim()}`
  return [
    { role: 'system', content: system },
    { role: 'user', content: user },
  ]
}

/**
 * Pull a usable HTML fragment out of a raw model reply: strip a leading/trailing
 * markdown code fence (```html … ``` or ``` … ```) and surrounding whitespace.
 * If the result has no tags at all, wrap the plain text in a paragraph so the
 * caller always gets insertable HTML. Pure I→O.
 */
export function extractAiHtml(content: string): string {
  let text = (content || '').trim()
  if (!text) return ''
  // ```html\n...\n```  or  ```\n...\n```
  const fence = text.match(/^```(?:html|HTML)?\s*\n?([\s\S]*?)\n?```$/)
  if (fence) text = fence[1].trim()
  if (!text) return ''
  // No markup at all → treat as plain text and keep line breaks.
  if (!/<[a-z][\s\S]*>/i.test(text)) {
    const esc = text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    return `<p>${esc.replace(/\n/g, '<br>')}</p>`
  }
  return text
}

/**
 * Allowlist-sanitise an AI HTML fragment before it enters the document. Strips
 * scripts/iframes/handlers and unsafe URLs while keeping the formatting + table
 * markup the editor renders. Pure I→O (DOMPurify is deterministic).
 */
export function sanitizeAiHtml(html: string): string {
  return DOMPurify.sanitize(html || '', {
    ALLOWED_TAGS: AI_HTML_ALLOWED_TAGS,
    ALLOWED_ATTR: AI_HTML_ALLOWED_ATTRS,
    FORBID_TAGS: ['script', 'style', 'iframe'],
  }).trim()
}

/**
 * Run a free-form editor instruction through the AI and return clean,
 * insertable HTML (or null when the model produced nothing usable). Throws only
 * on transport failure so the editor can surface a toast.
 */
export async function runAiInstruction(params: {
  instruction: string
  selectionHtml?: string
}): Promise<string | null> {
  const instruction = (params.instruction || '').trim()
  if (!instruction) return null
  const messages = buildInstructionMessages(instruction, params.selectionHtml || '')
  const res = await chatCompletion(messages, { temperature: 0.3, maxTokens: 1800 })
  const clean = sanitizeAiHtml(extractAiHtml(res?.content || ''))
  return clean || null
}

/** Build the safe inline-HTML for an AI body (escapes, keeps line breaks). */
export function aiBodyToHtml(body: string): string {
  const esc = (body || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
  const paras = esc
    .split(/\n{2,}/)
    .map((p) => `<p>${p.replace(/\n/g, '<br>')}</p>`)
    .join('')
  return paras || '<p></p>'
}
